export type Action =
  | 'moveLeft'
  | 'moveRight'
  | 'softDrop'
  | 'hardDrop'
  | 'rotateCW'
  | 'rotateCCW'
  | 'rotate180'
  | 'hold'
  | 'restart';

export const ACTIONS: Action[] = [
  'moveLeft',
  'moveRight',
  'softDrop',
  'hardDrop',
  'rotateCW',
  'rotateCCW',
  'rotate180',
  'hold',
  'restart',
];

export const ACTION_LABELS: Record<Action, string> = {
  moveLeft: 'Move Left',
  moveRight: 'Move Right',
  softDrop: 'Soft Drop',
  hardDrop: 'Hard Drop',
  rotateCW: 'Rotate CW',
  rotateCCW: 'Rotate CCW',
  rotate180: 'Rotate 180',
  hold: 'Hold',
  restart: 'Restart',
};

export const DEFAULT_KEYBINDINGS: Record<Action, string> = {
  moveLeft: 'ArrowLeft',
  moveRight: 'ArrowRight',
  softDrop: 'ArrowDown',
  hardDrop: 'Space',
  rotateCW: 'ArrowUp',
  rotateCCW: 'KeyZ',
  rotate180: 'KeyA',
  hold: 'KeyC',
  restart: 'KeyR',
};

export interface ControlSettings {
  keybindings: Record<Action, string>;
  das: number; // ms, 1-200
  arr: number; // ms, 0-100
}

const STORAGE_KEY = 'kale-downstack:controls';

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function defaultControlSettings(): ControlSettings {
  return { keybindings: { ...DEFAULT_KEYBINDINGS }, das: 150, arr: 20 };
}

export function loadControlSettings(): ControlSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultControlSettings();
    const parsed = JSON.parse(raw) as Partial<ControlSettings>;
    return {
      keybindings: { ...DEFAULT_KEYBINDINGS, ...(parsed.keybindings ?? {}) },
      das: clamp(Number(parsed.das) || 150, 1, 200),
      arr: clamp(Number(parsed.arr) ?? 20, 0, 100),
    };
  } catch {
    return defaultControlSettings();
  }
}

export function saveControlSettings(settings: ControlSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const specials: Record<string, string> = {
    Space: 'Space',
    ArrowLeft: '←',
    ArrowRight: '→',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ShiftLeft: 'LShift',
    ShiftRight: 'RShift',
    ControlLeft: 'LCtrl',
    ControlRight: 'RCtrl',
  };
  return specials[code] ?? code;
}

/**
 * Keyboard → game actions, with a DAS/ARR auto-repeat loop for horizontal
 * movement (ported timing model from the reference engine's press_left/
 * press_right: an initial move, then a DAS delay, then ARR-paced repeats —
 * ARR 0 jumps straight to the wall, same as the original).
 *
 * `onAction` receives `isRepeat: false` only for the original key press
 * (what finesse counts as "one input"); DAS/ARR-driven repeats pass `true`.
 */
export class InputController {
  private settings: ControlSettings;
  private enabled = true;
  private held = new Set<Action>();
  private dasTimer: number | null = null;
  private arrTimer: number | null = null;
  private softDropTimer: number | null = null;
  private onAction: (action: Action, isRepeat: boolean) => void;
  private codeToAction = new Map<string, Action>();

  constructor(onAction: (action: Action, isRepeat: boolean) => void, settings: ControlSettings) {
    this.onAction = onAction;
    this.settings = settings;
    this.rebuildCodeMap();
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.clearAllTimers();
  }

  updateSettings(settings: ControlSettings): void {
    this.settings = settings;
    this.rebuildCodeMap();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    this.clearAllTimers();
  }

  private rebuildCodeMap(): void {
    this.codeToAction.clear();
    for (const action of ACTIONS) this.codeToAction.set(this.settings.keybindings[action], action);
  }

  private clearAllTimers(): void {
    if (this.dasTimer !== null) window.clearTimeout(this.dasTimer);
    if (this.arrTimer !== null) window.clearInterval(this.arrTimer);
    if (this.softDropTimer !== null) window.clearInterval(this.softDropTimer);
    this.dasTimer = this.arrTimer = this.softDropTimer = null;
    this.held.clear();
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (!this.enabled) return;
    const action = this.codeToAction.get(e.code);
    if (!action) return;
    e.preventDefault();
    if (e.repeat) return; // ignore OS key-repeat; we run our own DAS/ARR loop
    this.held.add(action);

    if (action === 'moveLeft' || action === 'moveRight') {
      this.onAction(action, false);
      this.dasTimer = window.setTimeout(() => this.startAutoRepeat(action), this.settings.das);
    } else if (action === 'softDrop') {
      this.onAction(action, false);
      this.softDropTimer = window.setInterval(() => this.onAction('softDrop', true), 33);
    } else {
      this.onAction(action, false);
    }
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    const action = this.codeToAction.get(e.code);
    if (!action) return;
    this.held.delete(action);
    if (action === 'moveLeft' || action === 'moveRight') {
      if (this.dasTimer !== null) window.clearTimeout(this.dasTimer);
      if (this.arrTimer !== null) window.clearInterval(this.arrTimer);
      this.dasTimer = this.arrTimer = null;
    } else if (action === 'softDrop') {
      if (this.softDropTimer !== null) window.clearInterval(this.softDropTimer);
      this.softDropTimer = null;
    }
  };

  private startAutoRepeat(action: 'moveLeft' | 'moveRight'): void {
    if (!this.held.has(action)) return;
    if (this.settings.arr === 0) {
      this.onAction(action, true); // caller interprets as "slide to wall"
      return;
    }
    this.onAction(action, true);
    this.arrTimer = window.setInterval(() => {
      if (!this.held.has(action)) return;
      this.onAction(action, true);
    }, this.settings.arr);
  }
}
